import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { NotLinkedNotice, isNotLinked } from '@/components/NotLinkedNotice';
import { User as UserIcon, FileText, Receipt, Calendar, Clock, CheckCircle2, AlertTriangle, Download, ShieldCheck, MessageSquare, Plane, Activity, Inbox, Wallet, TrendingDown, Briefcase } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/currency';
import ActivityFeedPanel from '@/components/ActivityFeedPanel';
import EmployeeInboxTab from '@/components/EmployeeInboxTab';
import EmployeePortalPage from '@/pages/EmployeePortalPage';

const STATUS_PILL = {
  overdue:  'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
  due_soon: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  upcoming: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  ok:       'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
};

const ACK_STATUS_PILL = {
  acknowledged_by_employee: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  disputed: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
};

export default function MyPortalPage() {
  const [tab, setTab] = useState('work');
  const [overview, setOverview] = useState(null);
  const [profile, setProfile] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loans, setLoans] = useState({ loans: [], total_remaining: 0, active_count: 0, monthly_total: 0 });
  const [leaveBalance, setLeaveBalance] = useState({});
  const [loading, setLoading] = useState(true);

  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeFor, setDisputeFor] = useState(null);
  const [disputeReason, setDisputeReason] = useState('');

  const [leaveFormOpen, setLeaveFormOpen] = useState(false);
  const [notLinked, setNotLinked] = useState(false);
  const [blocked, setBlocked] = useState('');
  const [leaveForm, setLeaveForm] = useState({ leave_type: 'annual', start_date: '', end_date: '', reason: '' });

  const fetchAll = async () => {
    try {
      // Resolve the employee link once; unlinked accounts stop here instead of firing every /me/* call.
      try {
        const pr = await api.get('/me/profile');
        setProfile(pr.data);
      } catch (e) {
        if (isNotLinked(e)) { setNotLinked(true); return; }
        if (e?.response?.status === 403) { setBlocked(e.response.data?.detail?.message || 'Access to the portal is blocked.'); return; }
      }
      const [oR, psR, dR, lR, aR, lnR] = await Promise.allSettled([
        api.get('/me/dashboard'),
        api.get('/me/payslips'),
        api.get('/me/documents'),
        api.get('/me/leaves'),
        api.get('/me/attendance?days=60'),
        api.get('/me/loans'),
      ]);
      if (oR.status === 'fulfilled') setOverview(oR.value.data);
      if (psR.status === 'fulfilled') setPayslips(psR.value.data?.items || []);
      if (dR.status === 'fulfilled') setDocuments(dR.value.data?.items || []);
      if (lR.status === 'fulfilled') {
        setLeaves(lR.value.data?.items || []);
        setLeaveBalance(lR.value.data?.balance || {});
      }
      if (aR.status === 'fulfilled') setAttendance(aR.value.data?.items || []);
      if (lnR.status === 'fulfilled') setLoans(lnR.value.data || { loans: [] });
    } catch (e) {
      toast.error('Failed to load portal data');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const acknowledge = async (id) => {
    if (!window.confirm('Confirm receipt of this payslip? You won\'t be able to dispute it after.')) return;
    try {
      await api.post(`/me/payslips/${id}/acknowledge`);
      toast.success('Payslip acknowledged');
      fetchAll();
    } catch (e) { if (isNotLinked(e)) setNotLinked(true); else toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const submitDispute = async () => {
    if (disputeReason.trim().length < 5) { toast.error('Reason must be at least 5 characters'); return; }
    try {
      await api.post(`/me/payslips/${disputeFor.id}/dispute`, { reason: disputeReason });
      toast.success('Dispute filed — your manager has been notified');
      setDisputeOpen(false);
      setDisputeFor(null);
      setDisputeReason('');
      fetchAll();
    } catch (e) { if (isNotLinked(e)) setNotLinked(true); else toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const submitLeave = async () => {
    if (!leaveForm.start_date || !leaveForm.end_date) { toast.error('Pick start + end dates'); return; }
    try {
      await api.post('/me/leaves', leaveForm);
      toast.success('Leave request submitted');
      setLeaveFormOpen(false);
      setLeaveForm({ leave_type: 'annual', start_date: '', end_date: '', reason: '' });
      fetchAll();
    } catch (e) { if (isNotLinked(e)) setNotLinked(true); else toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-4 max-w-5xl mx-auto"><div className="h-32 animate-pulse bg-muted rounded-lg" /></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="my-portal-page">
        <NotLinkedNotice show={notLinked} />
        {blocked && <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="portal-blocked-notice">{blocked}</div>}
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-600 to-cyan-700 flex items-center justify-center shadow-lg">
            <UserIcon size={22} className="text-white" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">My Portal</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Welcome back, <strong>{profile?.name || overview?.name || 'Employee'}</strong>
              {overview?.designation && <span> · {overview.designation}</span>}
            </p>
          </div>
        </div>

        {!(notLinked || blocked) && (<>
        {/* Quick stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card>
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Pending Acknowledgments</div>
              <div className="text-2xl font-bold mt-1 text-amber-600 dark:text-amber-400" data-testid="stat-pending-ack">
                {overview?.pending_acknowledgments ?? 0}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">Payslips waiting for your confirmation</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Tracked Documents</div>
              <div className="text-2xl font-bold mt-1 text-blue-600 dark:text-blue-400" data-testid="stat-tracked-docs">
                {overview?.tracked_documents ?? 0}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">Visa, iqama, passport, etc.</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Leave Balance</div>
              <div className="text-2xl font-bold mt-1 text-emerald-600 dark:text-emerald-400" data-testid="stat-leave-balance">
                {Math.max(0, (leaveBalance.annual_entitled || 0) - (leaveBalance.used_this_year || 0))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                {leaveBalance.used_this_year || 0} used of {leaveBalance.annual_entitled || 0} annual days
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 w-full h-auto">
            <TabsTrigger value="work" data-testid="tab-work"><Briefcase size={12} className="mr-1.5" />Time &amp; Salary</TabsTrigger>
            <TabsTrigger value="overview" data-testid="tab-overview"><Clock size={12} className="mr-1.5" />Overview</TabsTrigger>
            <TabsTrigger value="inbox" data-testid="tab-inbox"><Inbox size={12} className="mr-1.5" />Inbox</TabsTrigger>
            <TabsTrigger value="payslips" data-testid="tab-payslips"><Receipt size={12} className="mr-1.5" />Payslips</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents"><FileText size={12} className="mr-1.5" />Documents</TabsTrigger>
            <TabsTrigger value="leaves" data-testid="tab-leaves"><Plane size={12} className="mr-1.5" />Leaves</TabsTrigger>
            <TabsTrigger value="loans" data-testid="tab-loans"><Wallet size={12} className="mr-1.5" />Loans</TabsTrigger>
            <TabsTrigger value="attendance" data-testid="tab-attendance"><Calendar size={12} className="mr-1.5" />Attendance</TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity"><Activity size={12} className="mr-1.5" />Activity</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="work" data-testid="tab-work-content">
            {/* Restored original portal: Time In/Out, shifts, salary record + payment confirmation, tasks, requests, letters */}
            <EmployeePortalPage embedded />
          </TabsContent>
          <TabsContent value="overview">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="font-outfit text-base">My Profile</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <Field label="Full Name" value={profile?.name} />
                  <Field label="Employee ID" value={profile?.id ? profile.id.slice(0, 8) + '…' : null} />
                  <Field label="Designation" value={profile?.position || profile?.designation} />
                  <Field label="Branch" value={profile?.branch_name} />
                  <Field label="Email" value={profile?.email} />
                  <Field label="Phone" value={profile?.phone || profile?.mobile} />
                  <Field label="Join Date" value={profile?.join_date || profile?.hire_date} />
                  <Field label="Nationality" value={profile?.nationality} />
                  <Field label="Salary" value={profile?.salary ? `${(profile.currency || 'SAR')} ${Number(profile.salary).toLocaleString()}` : '—'} />
                  <Field label="Bank Account" value={profile?.bank_account_last4 ? `••${profile.bank_account_last4}` : (profile?.bank_iban || profile?.iban || profile?.bank_account_number) ? `••${(profile.bank_iban || profile.iban || profile.bank_account_number).slice(-4)}` : null} />
                </div>
                <p className="text-[11px] text-muted-foreground mt-4 border-t border-border pt-3">
                  This is a read-only view. To update personal information, please contact HR.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Inbox — own pending requests + recent decisions */}
          <TabsContent value="inbox">
            <EmployeeInboxTab />
          </TabsContent>


          {/* Payslips */}
          <TabsContent value="payslips">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="font-outfit text-base">My Payslips</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 border-b border-border text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="text-left px-4 py-2">Period</th>
                        <th className="text-right px-4 py-2">Net Pay</th>
                        <th className="text-center px-4 py-2">Status</th>
                        <th className="text-right px-4 py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payslips.length === 0 ? (
                        <tr><td colSpan={4} className="text-center py-8 text-muted-foreground">No payslips yet. They will appear here once HR processes payroll.</td></tr>
                      ) : payslips.map(p => {
                        const ackStatus = p.acknowledgment_status || (p.acknowledged_at ? 'acknowledged_by_employee' : (p.dispute_reason ? 'disputed' : 'pending'));
                        return (
                          <tr key={p.id} className="border-b border-border hover:bg-muted/30" data-testid={`payslip-row-${p.id}`}>
                            <td className="px-4 py-2 font-medium">{p.period || (p.created_at || '').slice(0, 7)}</td>
                            <td className="px-4 py-2 text-right font-mono font-bold">{(p.currency || 'SAR')} {Number(p.net_pay || p.net_salary || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td className="px-4 py-2 text-center">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${ACK_STATUS_PILL[ackStatus] || ACK_STATUS_PILL.pending}`} data-testid={`payslip-status-${p.id}`}>
                                {ackStatus === 'acknowledged_by_employee' ? '✓ Acknowledged' : ackStatus === 'disputed' ? '⚠ Disputed' : 'Pending'}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-right">
                              <div className="inline-flex gap-1">
                                {!p.acknowledged_at && !p.dispute_reason && (
                                  <>
                                    <Button size="sm" variant="default" onClick={() => acknowledge(p.id)}
                                      className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`ack-btn-${p.id}`}>
                                      <CheckCircle2 size={11} className="mr-1" /> Acknowledge
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => { setDisputeFor(p); setDisputeOpen(true); }}
                                      className="h-7 px-2 text-xs text-rose-600 border-rose-300 hover:bg-rose-50" data-testid={`dispute-btn-${p.id}`}>
                                      <MessageSquare size={11} className="mr-1" /> Dispute
                                    </Button>
                                  </>
                                )}
                                {p.acknowledged_at && (
                                  <>
                                    <span className="text-[10px] text-muted-foreground italic px-2">
                                      on {(p.acknowledged_at || '').slice(0, 16).replace('T', ' ')}
                                    </span>
                                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                                      onClick={async () => {
                                        try {
                                          const r = await api.get(`/me/payslips/${p.id}/acknowledgment-pdf`, { responseType: 'blob' });
                                          const url = URL.createObjectURL(r.data);
                                          const a = document.createElement('a');
                                          a.href = url;
                                          a.download = `payslip-acknowledgment-${p.period || ''}.pdf`;
                                          a.click();
                                          setTimeout(() => URL.revokeObjectURL(url), 60000);
                                        } catch { toast.error('Download failed'); }
                                      }}
                                      data-testid={`proof-pdf-${p.id}`}>
                                      <Download size={11} className="mr-1" /> Proof PDF
                                    </Button>
                                  </>
                                )}
                                {p.dispute_reason && (
                                  <span className="text-[10px] text-rose-600 italic px-2 max-w-[200px] truncate" title={p.dispute_reason}>
                                    &quot;{p.dispute_reason}&quot;
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents */}
          <TabsContent value="documents">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="font-outfit text-base">My Documents</CardTitle></CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 border-b border-border text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="text-left px-4 py-2">Type</th>
                        <th className="text-left px-4 py-2">Number</th>
                        <th className="text-left px-4 py-2">Expiry</th>
                        <th className="text-center px-4 py-2">Days</th>
                        <th className="text-center px-4 py-2">Status</th>
                        <th className="text-right px-4 py-2">File</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documents.length === 0 ? (
                        <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No documents tracked.</td></tr>
                      ) : documents.map(d => (
                        <tr key={d.id} className="border-b border-border hover:bg-muted/30" data-testid={`mydoc-${d.id}`}>
                          <td className="px-4 py-2 font-medium capitalize">{(d.document_type || '').replace(/_/g, ' ')}</td>
                          <td className="px-4 py-2 font-mono text-xs">{d.document_number || '—'}</td>
                          <td className="px-4 py-2 font-mono text-xs">{d.expiry_date}</td>
                          <td className="px-4 py-2 text-center font-mono text-xs">{d.days_remaining ?? '—'}</td>
                          <td className="px-4 py-2 text-center">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_PILL[d.status] || STATUS_PILL.ok}`}>
                              {d.status === 'overdue' ? 'Overdue' : d.status === 'due_soon' ? 'Due soon' : d.status === 'upcoming' ? 'Upcoming' : 'OK'}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right">
                            {d.has_file ? (
                              <button onClick={async () => {
                                try {
                                  const r = await api.get(`/hr/documents/${d.id}/file`, { responseType: 'blob' });
                                  const url = URL.createObjectURL(r.data);
                                  window.open(url, '_blank');
                                  setTimeout(() => URL.revokeObjectURL(url), 60000);
                                } catch { toast.error('Download failed'); }
                              }} className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1">
                                <Download size={11} /> View
                              </button>
                            ) : <span className="text-xs text-muted-foreground">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Leaves */}
          <TabsContent value="leaves">
            <Card>
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="font-outfit text-base">My Leaves</CardTitle>
                <Button size="sm" onClick={() => setLeaveFormOpen(true)} data-testid="apply-leave-btn"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Plane size={12} className="mr-1" /> Apply for Leave
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 border-b border-border text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="text-left px-4 py-2">Type</th>
                        <th className="text-left px-4 py-2">Start</th>
                        <th className="text-left px-4 py-2">End</th>
                        <th className="text-right px-4 py-2">Days</th>
                        <th className="text-center px-4 py-2">Status</th>
                        <th className="text-left px-4 py-2 hidden md:table-cell">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaves.length === 0 ? (
                        <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No leave records yet.</td></tr>
                      ) : leaves.map(l => (
                        <tr key={l.id} className="border-b border-border hover:bg-muted/30">
                          <td className="px-4 py-2 capitalize">{l.leave_type || 'annual'}</td>
                          <td className="px-4 py-2 font-mono text-xs">{(l.start_date || '').slice(0, 10)}</td>
                          <td className="px-4 py-2 font-mono text-xs">{(l.end_date || '').slice(0, 10)}</td>
                          <td className="px-4 py-2 text-right font-mono">{l.days_count || 0}</td>
                          <td className="px-4 py-2 text-center">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              (l.status === 'approved') ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                              : (l.status === 'rejected') ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400'
                              : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'}`}>
                              {l.status || 'pending'}
                            </span>
                          </td>
                          <td className="px-4 py-2 hidden md:table-cell text-xs text-muted-foreground truncate max-w-[200px]">{l.reason || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Loans — repayment schedule (iter812) */}
          <TabsContent value="loans">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <Card data-testid="loan-kpi-outstanding">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-100 text-amber-700"><Wallet size={18} /></div>
                  <div>
                    <div className="text-lg font-bold tabular-nums">{formatCurrency(loans.total_remaining || 0)}</div>
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Outstanding Balance</div>
                  </div>
                </CardContent>
              </Card>
              <Card data-testid="loan-kpi-monthly">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100 text-blue-700"><TrendingDown size={18} /></div>
                  <div>
                    <div className="text-lg font-bold tabular-nums">{formatCurrency(loans.monthly_total || 0)}</div>
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Monthly Deduction</div>
                  </div>
                </CardContent>
              </Card>
              <Card data-testid="loan-kpi-active">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-100 text-emerald-700"><Receipt size={18} /></div>
                  <div>
                    <div className="text-lg font-bold tabular-nums">{loans.active_count || 0}</div>
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Active Loans</div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {(!loans.loans || loans.loans.length === 0) ? (
              <Card><CardContent className="p-8 text-center text-muted-foreground" data-testid="loans-empty">
                You have no loans or advances on record. 🎉
              </CardContent></Card>
            ) : (
              <div className="space-y-3">
                {loans.loans.map((ln) => (
                  <Card key={ln.id} data-testid={`loan-card-${ln.id}`} className={ln.status === 'completed' ? 'opacity-70' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <div className="font-semibold flex items-center gap-2">
                            {ln.reason || 'Loan / Advance'}
                            <span className={`text-[10px] px-2 py-0.5 rounded-full ${ln.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                              {ln.status === 'completed' ? 'Cleared' : 'Active'}
                            </span>
                          </div>
                          <div className="text-[12px] text-muted-foreground mt-0.5">
                            Principal {formatCurrency(ln.principal_amount)} · {ln.paid_installments}/{ln.total_installments} installments paid
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold tabular-nums text-amber-700">{formatCurrency(ln.remaining_balance)}</div>
                          <div className="text-[11px] text-muted-foreground">remaining</div>
                        </div>
                      </div>

                      {/* progress bar */}
                      <div className="mt-3 h-2 rounded-full bg-muted overflow-hidden">
                        <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${Math.min(100, ln.progress_pct)}%` }} />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mt-3 text-center">
                        <div>
                          <div className="text-sm font-semibold tabular-nums">{formatCurrency(ln.monthly_installment)}</div>
                          <div className="text-[10px] uppercase text-muted-foreground">Monthly</div>
                        </div>
                        <div>
                          <div className="text-sm font-semibold tabular-nums" data-testid={`loan-months-left-${ln.id}`}>
                            {ln.status === 'completed' ? '—' : `${ln.months_left} mo`}
                          </div>
                          <div className="text-[10px] uppercase text-muted-foreground">Months Left</div>
                        </div>
                        <div>
                          <div className="text-sm font-semibold" data-testid={`loan-clear-date-${ln.id}`}>
                            {ln.status === 'completed' ? 'Cleared' : (ln.projected_clear_period || '—')}
                          </div>
                          <div className="text-[10px] uppercase text-muted-foreground">Projected Clear</div>
                        </div>
                      </div>

                      {(ln.installments || []).length > 0 && (
                        <div className="mt-3 border-t pt-2" data-testid={`loan-deduction-history-${ln.id}`}>
                          <div className="text-[11px] font-medium text-muted-foreground mb-1.5">Deduction History ({ln.installments.length})</div>
                          <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                            {ln.installments.map((inst, idx) => (
                              <div key={inst.id || idx} className="flex items-center justify-between gap-2 text-xs" data-testid={`loan-installment-row-${idx}`}>
                                <span className="text-muted-foreground truncate">
                                  {(inst.date || '').slice(0, 10)}
                                  {inst.period ? ` · ${inst.period}` : ''}
                                  {inst.payment_mode === 'deduction' ? ' · payroll auto-deduct' : inst.payment_mode ? ` · ${inst.payment_mode}` : ''}
                                </span>
                                <span className="font-mono font-medium text-rose-600 whitespace-nowrap tabular-nums">−{formatCurrency(inst.amount || 0)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>


          {/* Activity */}
          <TabsContent value="activity">
            <ActivityFeedPanel scope="me" limit={100} title="My Activity" />
          </TabsContent>

          {/* Attendance */}
          <TabsContent value="attendance">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="font-outfit text-base">Attendance — last 60 days</CardTitle></CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 border-b border-border text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="text-left px-4 py-2">Date</th>
                        <th className="text-left px-4 py-2">In</th>
                        <th className="text-left px-4 py-2">Out</th>
                        <th className="text-right px-4 py-2">Hours</th>
                        <th className="text-center px-4 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendance.length === 0 ? (
                        <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">No attendance entries in the last 60 days.</td></tr>
                      ) : attendance.map((a, i) => (
                        <tr key={i} className="border-b border-border hover:bg-muted/30">
                          <td className="px-4 py-2 font-mono text-xs">{a.date}</td>
                          <td className="px-4 py-2 font-mono text-xs">{a.check_in || (a.time_in ? new Date(a.time_in).toLocaleTimeString() : '—')}</td>
                          <td className="px-4 py-2 font-mono text-xs">{a.check_out || (a.time_out ? new Date(a.time_out).toLocaleTimeString() : '—')}</td>
                          <td className="px-4 py-2 text-right font-mono text-xs">{a.hours_worked ?? '—'}</td>
                          <td className="px-4 py-2 text-center">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              a.status === 'present' ? 'bg-emerald-100 text-emerald-700' :
                              a.status === 'late' ? 'bg-amber-100 text-amber-700' :
                              a.status === 'absent' ? 'bg-rose-100 text-rose-700' : 'bg-muted'
                            }`}>{a.status || '—'}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        </>)}

        {/* Dispute dialog */}
        <Dialog open={disputeOpen} onOpenChange={setDisputeOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Dispute payslip — {disputeFor?.period}</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-sm">Reason for dispute *</Label>
                <Textarea value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)}
                  placeholder="e.g. Overtime hours don't match my timesheet — should be 18h not 12h…"
                  className="mt-1" rows={4}
                  data-testid="dispute-reason-input" />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Your manager will be notified and will reach out to resolve. You won't be able to acknowledge until the dispute is resolved.
                </p>
              </div>
            </div>
            <NotLinkedNotice show={notLinked} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setDisputeOpen(false)}>Cancel</Button>
              <Button onClick={submitDispute} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="submit-dispute-btn">
                Submit Dispute
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Apply leave dialog */}
        <Dialog open={leaveFormOpen} onOpenChange={setLeaveFormOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Apply for Leave</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={leaveForm.leave_type} onValueChange={(v) => setLeaveForm({ ...leaveForm, leave_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="annual">Annual</SelectItem>
                    <SelectItem value="sick">Sick</SelectItem>
                    <SelectItem value="emergency">Emergency</SelectItem>
                    <SelectItem value="unpaid">Unpaid</SelectItem>
                    <SelectItem value="hajj">Hajj</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Start Date</Label>
                  <Input type="date" value={leaveForm.start_date} onChange={(e) => setLeaveForm({ ...leaveForm, start_date: e.target.value })} data-testid="leave-start" />
                </div>
                <div>
                  <Label className="text-xs">End Date</Label>
                  <Input type="date" value={leaveForm.end_date} onChange={(e) => setLeaveForm({ ...leaveForm, end_date: e.target.value })} data-testid="leave-end" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Reason</Label>
                <Textarea value={leaveForm.reason} onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })} rows={3} />
              </div>
            </div>
            <NotLinkedNotice show={notLinked} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setLeaveFormOpen(false)}>Cancel</Button>
              <Button onClick={submitLeave} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="submit-leave-btn">
                Submit Request
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{label}</div>
      <div className="text-sm text-foreground mt-0.5 break-words">{value || <span className="text-muted-foreground">—</span>}</div>
    </div>
  );
}

